import {
    ConfirmIncomeDeleteSheet,
    formatIncomeType,
    getIncomeOwnerDisplayFormatter,
    type MembershipIncome,
} from '@fruitful/member-profile';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useCallback, useState } from 'react';
import { type GestureResponderEvent } from 'react-native/Libraries/Types/CoreEventTypes';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    CircleXIcon,
    EditIcon,
    Icon,
    PlusIcon,
    Row,
    RowList,
    ScreenContainer,
    Spinner,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import LogRocketFacade from '../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../logger/Logger';
import useDeleteMembershipIncome from '../hooks/useDeleteMembershipIncome';
import useGetMembershipIncomes from '../hooks/useGetMembershipIncomes';
import {
    getMemberProfileUserSubtitle,
    MemberProfileLogger,
    transformUserToMemberProfileMembership,
} from '../utils/memberProfileUtils';

export const Income = () => {
    LogRocketFacade.tagPage('ProfileIncome');

    const toast = useToastController();
    const authedUser = useAuthedUser();
    const { userId } = useLocalSearchParams<{ userId?: string }>();

    // Use provided userId or fallback to authenticated user. Find the target user.
    const targetUserId = userId ?? authedUser.id;
    const targetUser = [authedUser, authedUser.partnerMember].find(
        (user) => user?.id === targetUserId,
    );
    const {
        data: membershipIncomes,
        isLoading: isLoadingMembershipIncomes,
        refetch: refetchMembershipIncomes,
    } = useGetMembershipIncomes({});

    const membership = transformUserToMemberProfileMembership(authedUser);
    const { mutateAsync: deleteMembershipIncome } = useDeleteMembershipIncome();
    const [incomeIdToDelete, setIncomeIdToDelete] = useState<string | null>(
        null,
    );

    // Filter income records to only those for the target user.
    const incomeData = (membershipIncomes ?? []).filter(
        (income: MembershipIncome) => {
            return income.belongsToUserId === targetUserId;
        },
    );

    // Should not happen, but added as a defensive check.
    if (targetUser == null) {
        captureException(new Error('Target user not found for income page'));
        return <Redirect href="/profile/financial-profile" />;
    }

    const onAddIncome = () => {
        router.navigate({
            pathname: '/profile/add-income',
            params: { userId: targetUserId },
        });
    };

    const onEditIncome = (incomeId: string) => {
        router.navigate({
            pathname: '/profile/edit-income',
            params: { userId: targetUserId, incomeId },
        });
    };

    const onConfirmDelete = useCallback(async () => {
        if (incomeIdToDelete == null) return;
        try {
            await deleteMembershipIncome({
                urlParams: { id: incomeIdToDelete },
            });
            void refetchMembershipIncomes();
            setIncomeIdToDelete(null);
            toast.show('Income deleted successfully', {
                duration: 2000,
            });
        } catch (error) {
            captureException(error);
            toast.show('Failed to delete income', {
                duration: 2000,
            });
        }
    }, [
        incomeIdToDelete,
        deleteMembershipIncome,
        refetchMembershipIncomes,
        toast,
    ]);

    const { users } = membership;
    const formatIncomeOwner = getIncomeOwnerDisplayFormatter(
        users,
        MemberProfileLogger,
    );

    return (
        <ScreenContainer
            title="Household income"
            hasBackButton={true}
            subtitle={getMemberProfileUserSubtitle(authedUser, targetUser)}
        >
            {isLoadingMembershipIncomes ? (
                <Spinner />
            ) : (
                <YStack gap={24} justifyContent="space-between">
                    <RowList>
                        {incomeData.map((income) => (
                            <Row
                                key={income.id}
                                title={income.description}
                                subtitle={`${formatIncomeOwner(income.belongsToUserId)} \u25AA ${formatIncomeType(income.type)}`}
                                onPress={() => {
                                    onEditIncome(income.id);
                                }}
                                rightContent={
                                    <XStack gap={20}>
                                        <View
                                            onPress={() => {
                                                onEditIncome(income.id);
                                            }}
                                        >
                                            <Icon
                                                icon={EditIcon}
                                                size={20}
                                                color="$black"
                                            />
                                        </View>
                                        {income.id != null && (
                                            <View
                                                onPress={(
                                                    e: GestureResponderEvent,
                                                ) => {
                                                    e.stopPropagation();
                                                    setIncomeIdToDelete(
                                                        income.id,
                                                    );
                                                }}
                                            >
                                                <Icon
                                                    icon={CircleXIcon}
                                                    size={20}
                                                    color="$black"
                                                />
                                            </View>
                                        )}
                                    </XStack>
                                }
                            />
                        ))}
                    </RowList>

                    <Button
                        size={ButtonSize.LARGE}
                        variant={ButtonVariant.SECONDARY}
                        icon={PlusIcon}
                        text="Add income"
                        onPress={onAddIncome}
                    />
                </YStack>
            )}
            <ConfirmIncomeDeleteSheet
                isOpen={incomeIdToDelete != null}
                onClose={() => {
                    setIncomeIdToDelete(null);
                }}
                onConfirm={onConfirmDelete}
            />
        </ScreenContainer>
    );
};
